import { motion } from 'motion/react';
import { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}

export function Button({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  className = '',
  onClick,
  style,
  type = "button",
  disabled = false
}: ButtonProps) {
  const variants = {
    primary: 'bg-[#ff5d22] text-white shadow-2xl shadow-[#ff5d22]/20 hover:bg-[#d41820] hover:shadow-[#ff5d22]/20 transition-all duration-500',
    secondary: 'bg-white border border-black/5 text-black hover:bg-black hover:text-white transition-all duration-500 shadow-sm',
    ghost: 'bg-transparent text-black/40 hover:text-black hover:bg-black/5 transition-all duration-300',
    outline: 'bg-transparent border border-black/10 text-black hover:border-black/30 hover:bg-black/5 transition-all duration-300',
  };

  const sizes = {
    sm: 'px-4 sm:px-6 py-2.5 sm:py-3 text-[9px] sm:text-[10px] uppercase font-black tracking-[0.12em] sm:tracking-widest',
    md: 'px-5 sm:px-8 py-3 sm:py-4 text-[10px] sm:text-[11px] uppercase font-black tracking-[0.14em] sm:tracking-[0.2em]',
    lg: 'px-7 sm:px-12 py-4 sm:py-6 text-[11px] sm:text-xs uppercase font-black tracking-[0.16em] sm:tracking-[0.3em]',
  };

  return (
    <motion.button
      type={type}
      disabled={disabled}
      whileHover={disabled ? {} : { scale: 1.05, y: -2 }}
      whileTap={disabled ? {} : { scale: 0.95 }}
      onClick={onClick}
      style={style}
      className={`max-w-full rounded-2xl inline-flex items-center justify-center gap-2 text-center leading-tight whitespace-normal transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </motion.button>
  );
}
